import React, { useCallback, useMemo } from "react";
import DataGrid, {
	Column,
	Pager,
	Paging,
	Sorting,
} from "devextreme-react/data-grid";
import CustomStore from "devextreme/data/custom_store";
import { getDeleteRequests } from "../../api/apiServiceFetch";
import { utcToLocalDateTime } from "../../components/helper";
import {
	getModerationRowActions,
	hasModerationRowActions,
} from "../moderationRowActions";
import "./DeleteRequests.scss";

const getListFromResponse = (payload) => {
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.items)) return payload.items;
	if (Array.isArray(payload?.requests)) return payload.requests;
	if (Array.isArray(payload?.deleteRequests)) return payload.deleteRequests;
	return [];
};

const getNameSurname = (request) => {
	const explicitName =
		request.nameSurname ||
		request.name_surname ||
		request.fullName ||
		request.user?.fullName;

	if (explicitName) return explicitName;

	return [
		request.name || request.firstName || request.user?.name || request.user?.firstName,
		request.surname ||
			request.lastName ||
			request.user?.surname ||
			request.user?.lastName,
	]
		.filter(Boolean)
		.join(" ");
};

const getValue = (...values) => {
	const value = values.find(
		(item) => item !== undefined && item !== null && item !== "",
	);
	return value ?? "-";
};

const getDateTime = (request) =>
	getValue(
		request.dateTime,
		request.datetime,
		request.createdAt,
		request.createdDate,
		request.cDate,
		request.date,
		request.requestedAt,
		request.deleteRequestedAt,
		request.deletedAt,
	);

const formatDateTime = (value) => {
	if (value === "-") return value;

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;

	return utcToLocalDateTime(value);
};

const normalizeDeleteRequest = (request, index) => {
	const dateTimeRaw = getDateTime(request);
	const id = getValue(request.id, request.requestId, request.deleteRequestId);

	return {
		id,
		rowId: `${getValue(
			request.id,
			request.requestId,
			request.deleteRequestId,
			request.userId,
			request.userid,
			request.user?.id,
			request.user?.userId,
			index,
		)}-${index}`,
		nameSurname: getValue(getNameSurname(request)),
		email: getValue(request.email, request.user?.email),
		userid: getValue(request.userId, request.userid, request.user?.id),
		phone: getValue(request.phone, request.phoneNumber, request.user?.phone),
		username: getValue(request.username, request.userName, request.user?.username),
		reason: getValue(request.reason),
		legacyUserId: getValue(request.legacyUserId, request.user?.legacyUserId),
		dateTime: formatDateTime(dateTimeRaw),
		dateTimeRaw,
	};
};

const DeleteRequests = ({
	statusActions = [],
	onStatusUpdate,
	onOpenDetails,
}) => {
	const showRowActions = hasModerationRowActions({
		statusActions,
		onStatusUpdate,
		onOpenDetails,
	});

	const renderRowActions = useCallback(
		({ data }) => {
			const actions = getModerationRowActions({
				row: data,
				statusActions,
				onStatusUpdate,
				onOpenDetails,
			});

			return (
				<div className="delete-requests__row-actions">
					{actions.map((action) => (
						<button key={action.key} type="button" onClick={action.onClick}>
							{action.label}
						</button>
					))}
				</div>
			);
		},
		[statusActions, onStatusUpdate, onOpenDetails],
	);

	const deleteRequestsStore = useMemo(
		() =>
			new CustomStore({
				key: "id",
				load: async (loadOptions) => {
					const take = loadOptions.take || 20;
					const skip = loadOptions.skip || 0;
					const pageNumber = Math.floor(skip / take) + 1;

					try {
						const response = await getDeleteRequests({
							pageNumber,
							pageSize: take,
						});
						const rows = getListFromResponse(response).map(
							normalizeDeleteRequest,
						);

						return {
							data: rows,
							totalCount:
								response?.totalCount ?? response?.TotalCount ?? rows.length,
						};
					} catch (err) {
						console.error("Failed to load delete requests:", err);
						throw new Error("Не удалось загрузить запросы на удаление.");
					}
				},
			}),
		[],
	);

	return (
		<div className="delete-requests">
			<h2>Запросы на удаление</h2>

			<div className="delete-requests__table">
				<DataGrid
					dataSource={deleteRequestsStore}
					keyExpr="id"
					remoteOperations={{ paging: true, sorting: false, filtering: false }}
					showBorders={true}
					wordWrapEnabled={true}
					columnAutoWidth={true}
					rowAlternationEnabled={true}
					noDataText="Нет запросов"
				>
					<Sorting mode="multiple" />
					<Paging defaultPageSize={20} />
					<Pager
						showPageSizeSelector={true}
						allowedPageSizes={[10, 20, 50]}
						showInfo={true}
					/>
					<Column dataField="nameSurname" caption="name/surname" />
					<Column dataField="email" caption="email" />
					<Column dataField="userid" caption="userid" />
					<Column dataField="legacyUserId" caption="TG id" />
					<Column dataField="phone" caption="phone" />
					<Column dataField="username" caption="username" />
					<Column dataField="reason" caption="reason" />
					<Column
						dataField="dateTime"
						caption="date time"
						sortOrder="desc"
						calculateSortValue={(data) => data.dateTimeRaw}
					/>
					{showRowActions && (
						<Column
							caption="Действие"
							width={180}
							allowSorting={false}
							cellRender={renderRowActions}
						/>
					)}
				</DataGrid>
			</div>
		</div>
	);
};

export default DeleteRequests;
